<script setup lang="ts" generic="T">
import DataTable from 'primevue/datatable'

const props = defineProps<{
    value: T[]
    loading?: boolean
    selection?: T[]
    rowKey?: string
    sortField?: string
    sortOrder?: 1 | -1 | null
    /**
     * Map of expanded rows keyed by `rowKey` value. Pass `{}` for no expansion.
     * Omit entirely to opt out of expansion (no slot rendered).
     */
    expandedRows?: Record<string, boolean>
    /**
     * Per-row class function — returns Tailwind class string(s) for a given row.
     * Forwarded to PrimeVue DataTable :row-class.
     */
    rowClass?: (data: T) => string | string[] | undefined
}>()

defineEmits<{
    (e: 'update:selection', v: T[]): void
    (e: 'update:expandedRows', v: Record<string, boolean>): void
    (e: 'row-click', v: T): void
    (e: 'sort', v: { sortField: string; sortOrder: 1 | -1 }): void
}>()
</script>

<template>
    <DataTable
        :value="props.value"
        :loading="props.loading"
        :selection="props.selection"
        :data-key="props.rowKey"
        :sort-field="props.sortField"
        :sort-order="props.sortOrder ?? undefined"
        :expanded-rows="props.expandedRows"
        :row-class="props.rowClass"
        @update:selection="$emit('update:selection', $event)"
        @update:expanded-rows="$emit('update:expandedRows', $event as Record<string, boolean>)"
        @row-click="e => $emit('row-click', e.data)"
        @sort="e => $emit('sort', { sortField: e.sortField as string, sortOrder: e.sortOrder as 1 | -1 })"
    >
        <slot />
        <template v-if="$slots.expansion" #expansion="slotProps">
            <slot name="expansion" v-bind="slotProps" />
        </template>
    </DataTable>
</template>
